import { Injectable, OnDestroy } from '@angular/core';
import moment from 'moment';
import { RepoDeploymentService } from '../../data/repositories/repo-deployment.service';
import { SharedDataService, Version } from '../../data/services/shared-data.service';
import { AppSettingsService } from './app-settings.service';
import { BxLogger } from './bx-logger.service';
import { DialogService } from './dialog.service';
import { EditTrackerService } from './edit-tracker.service';

@Injectable({ providedIn: 'root' })
export class AppVersionService implements OnDestroy {
    private interval: number = 0;
    private everyMin = 1000 * 60;
    private everyHalfHour = this.everyMin * 30;
    private displayingVersionPrompt = false;

    public readonly APP_VERSION_DATE_FORMAT_FULL = 'dddd, MMMM DD, YYYY h:mm:ss A';

    constructor(
        private repoDeployment: RepoDeploymentService,
        private sharedData: SharedDataService,
        private logger: BxLogger,
        private appSettingsService: AppSettingsService,
        private dialogService: DialogService,
        private editTracker: EditTrackerService
    ) {}

    async start() {
        this.interval = window.setInterval(() => this.checkAndPromptOnNewVersion(), this.everyMin);
    }

    ngOnDestroy() {
        if (this.interval) window.clearInterval(this.interval);
    }

    getVersionDateAsMoment(date: string): moment.Moment {
        // In version.json, the Octopus variable has been changed to include formatting
        // We need to support both old and new formats here
        let result = moment.utc(date, this.APP_VERSION_DATE_FORMAT_FULL, 'en');
        if (!result.isValid()) {
            // Try the new format
            result = moment.utc(date);
        }
        return result;
    }

    versionIsValid(v: Version) {
        if (!v || !v.number || !v.date) return false;
        const versionDate = this.getVersionDateAsMoment(v.date);
        return versionDate.isValid();
    }

    versionsAreDifferent(current: Version, latest: Version): boolean {
        if (!this.versionIsValid(current) || !this.versionIsValid(latest)) return false;
        const currentUtc = this.getVersionDateAsMoment(current.date);
        const latestUtc = this.getVersionDateAsMoment(latest.date);
        const versionsHaveDifferentDate = !currentUtc.isSame(latestUtc, 'second');
        const versionsHaveDifferentNumbers = latest.number !== current.number;
        return versionsHaveDifferentDate || versionsHaveDifferentNumbers;
    }

    checkVersions(current: Version, latest: Version) {
        this.sharedData.Common.Version = current;
        // Reset the HasNewVersionSince
        if (!this.sharedData.Common.HasNewVersion) {
            this.sharedData.Common.HasNewVersionSince = undefined;
        }
        // route-handler.service.ts > onSuccess automatically reloads the page
        // if this is set to true...
        this.sharedData.Common.HasNewVersion = this.versionsAreDifferent(current, latest);
        if (this.sharedData.Common.HasNewVersion && this.sharedData.Common.HasNewVersionSince === undefined) {
            // Set this so we can determine to show the dialog 2 hours after a new version was detected
            this.sharedData.Common.HasNewVersionSince = new Date();
        }
    }

    shouldDisplayNewVersionPrompt() {
        if (!this.sharedData.Common.HasNewVersion) return false;
        // If the user hasn't navigated and it's been 2 or more hours, and we're not editing or displaying the prompt....
        const hoursSinceNewVersionDetected = moment().diff(moment(this.sharedData.Common.HasNewVersionSince), 'hour');
        return hoursSinceNewVersionDetected >= 2 && !this.editTracker.isTracking && !this.displayingVersionPrompt;
    }

    async checkAndPromptOnNewVersion() {
        if (this.appSettingsService.online) {
            try {
                const latest = await this.repoDeployment.GetLatestVersion();
                const current = this.repoDeployment.GetCurrentVersion();
                this.checkVersions(current, latest);
                if (this.shouldDisplayNewVersionPrompt()) {
                    const params = {
                        title: 'A new version of Buildxact is available',
                        text: 'Refresh to get the latest version (this should only take a few seconds).',
                        okText: 'Refresh',
                        cancelText: 'Later',
                    };
                    this.displayingVersionPrompt = true;
                    this.dialogService.show(params).then(
                        () => {
                            window.location.reload();
                        },
                        () => {
                            // Supress checks for 30 minutes if user clicks 'Later' prompt
                            this.displayingVersionPrompt = false;
                            if (this.interval) clearInterval(this.interval);

                            this.interval = window.setInterval(() => this.checkAndPromptOnNewVersion(), this.everyHalfHour);
                        }
                    );
                }
            } catch (err) {
                this.logger.exception(err);
            }
        }
    }
}
